import { readAttributionCapture } from "@/lib/acquisitionCapture.ts";
import { api } from "@/lib/http.ts";
import type { AttributionCaptureReq } from "@/types/acquisition.ts";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

/**
 * Hands the campaign parameters a page load arrived with to the API, which
 * remembers them on its own origin for the sign-up that follows. Attribution
 * never affects the visitor, so a failure is dropped silently.
 *
 * Marketing measurement only: nothing in the app may read these parameters to
 * identify the visitor or change what they see.
 */
export const AttributionCapture = () => {
  const { mutate } = useMutation({
    mutationFn: async (body: AttributionCaptureReq): Promise<void> => {
      await api.post<void>("/api/p/attribution", body);
    },
  });
  // Read during render, not in the effect: this renders before the routes, and
  // some of them (the claim page) strip the query string while rendering, so by
  // the time effects run the parameters are gone.
  const [body] = useState(() => readAttributionCapture(window.location, document.referrer));
  // StrictMode runs effects twice; the landing should be recorded once.
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    if (body !== null) mutate(body);
  }, [body, mutate]);

  return null;
};
